import useLogger from 'composables/logger';
import useSetTimeout from 'composables/set-timeout';

let ws: WebSocket;

/**
 * Creates a new WebSocket connection.
 */
export function createConnection() {
  ws = new WebSocket(`wss://${location.host}/socket`);
  return ws;
}

/**
 * Creates a WebSocket connection and adds the necessary event listeners.
 */
export async function connect() {
  ws = createConnection();

  ws.onerror = (event) => {
    const logger = useLogger();
    // The argument is an "Event", but it's also an error?
    logger.error('WebSocket error', <Error>(<unknown>event));
  };

  ws.onclose = async () => {
    const wait = useSetTimeout();

    console.info('WebSocket connection closed, reconnecting...');
    await wait(3000); // waiting 3 seconds, giving time for the server to become available
    void connect();

    await WebSocketClient.ready();
    console.info('WebSocket reconnected.');
  };

  ws.addEventListener('message', (event) => {
    WebSocketClient.handleMessage((<Buffer>event.data).toString());
  });

  await WebSocketClient.ready();
}

type WebSocketMessageHandler = (payload: unknown) => void;

export class WebSocketClient {
  static eventCallbacks: { [action: string]: WebSocketMessageHandler[] } = {};

  /**
   * Handles JSON (string) messages and calls associated listeners.
   */
  static handleMessage(jsonPayload: string) {
    const { action, payload } = <WebSocketMessagePayload>JSON.parse(jsonPayload);
    const callbacks = this.eventCallbacks[action] || [];
    callbacks.forEach((callback) => {
      callback(payload);
    });
  }

  /**
   * Removes a given listener from the given action listeners.
   */
  static off(action: string, callback: WebSocketMessageHandler) {
    const existingCallbacks = this.eventCallbacks[action];
    if (existingCallbacks) {
      this.eventCallbacks[action] = existingCallbacks.filter((existingCallback) => existingCallback !== callback);
    }
  }

  /**
   * Adds a listener for the given action.
   */
  static on(action: string, callback: WebSocketMessageHandler) {
    const existingCallbacks = this.eventCallbacks[action] || [];
    this.eventCallbacks[action] = [...existingCallbacks, callback];
  }

  /**
   * Adds a listener for the given action that is removed after it's triggered.
   */
  static once(action: string, callback: WebSocketMessageHandler) {
    const onceCallback = (payload: unknown) => {
      callback(payload);
      this.off(action, onceCallback);
    };
    this.on(action, onceCallback);
  }

  /**
   * Listens for the WebSocket "open" event and resolves when it's opened.
   */
  static ready() {
    return new Promise<void>((resolve) => {
      const onOpenCallback = () => {
        ws.removeEventListener('open', onOpenCallback);
        resolve();
      };
      ws.addEventListener('open', onOpenCallback);
    });
  }

  /**
   * Sends a WebSocket message using the given action and payload (stringified).
   */
  static send(action: string, payload?: unknown) {
    ws.send(
      JSON.stringify({
        action,
        payload,
      })
    );
  }
}

export default function useWebsocket() {
  return {
    connect,
    ws: WebSocketClient,
  };
}
